import React, { useState, useEffect } from 'react';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import { useAuth } from '../../../contexts/AuthContext';
import inventoryApi from '../../../services/inventoryApi';
import adminAPI from '../../../services/adminApi';
import { openSupplierPaymentReport } from '../../../utils/openInventoryReport';
import { canPrintSupplierPayment } from '../../../utils/documentPrintGate';
import ChequePrintAction, { supplierPaymentChequeLines } from '../../Common/ChequePrintAction';
import PaymentReversalBanner from '../../Common/PaymentReversalBanner';
import { DoubleEntryModal } from '../../Finance/DoubleEntries/DoubleEntryPanel';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const errorMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

const SupplierPaymentView = ({ paymentId: propPaymentId, onClose }) => {
  const { id: paramId } = useParams();
  const handleBack = useDetailBack('/inventory/supplier-payments');
  const { hasPermission } = useAuth();
  const isModal = Boolean(propPaymentId);
  const id = propPaymentId || paramId;

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [companyInfo, setCompanyInfo] = useState({});
  const [showDoubleEntries, setShowDoubleEntries] = useState(false);

  useEffect(() => {
    if (id) fetchPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const locationId = localStorage.getItem('X-Location-Id') || localStorage.getItem('lastUsedLocationId');
        if (!locationId) return;
        const res = await adminAPI.getLocationById(locationId);
        const loc = res.data?.data || res.data || {};
        const addr = loc.address || {};
        setCompanyInfo({
          name: loc.name || loc.locationName || '',
          street: addr.street || addr.addressLine1 || '',
          city: addr.city || '',
          zipCode: addr.zipCode || addr.postalCode || '',
        });
      } catch (e) {
        console.warn('[SupplierPaymentView] could not load location info:', e.message);
      }
    })();
  }, []);

  const handlePrint = async () => {
    await openSupplierPaymentReport(payment, companyInfo);
  };

  const fetchPayment = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getSupplierPayment(id);
      setPayment(response.data);
    } catch (err) {
      console.error('Error fetching payment:', err);
      toast.error(errorMessage(err, 'Failed to load payment details'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (isModal && onClose) onClose();
    else handleBack();
  };

  const openAction = (setter) => { setActionComment(''); setter(true); };

  const confirmApprove = async () => {
    try {
      setApproving(true);
      await inventoryApi.approveSupplierPayment(id, { comments: actionComment.trim() || 'Approved' });
      toast.success('Payment approved');
      setShowApproveModal(false);
      fetchPayment();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to approve payment'));
    } finally {
      setApproving(false);
    }
  };

  const confirmReject = async () => {
    if (!actionComment.trim()) { toast.error('Rejection reason is required'); return; }
    try {
      setApproving(true);
      await inventoryApi.rejectSupplierPayment(id, { comments: actionComment.trim() });
      toast.success('Payment rejected');
      setShowRejectModal(false);
      fetchPayment();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to reject payment'));
    } finally {
      setApproving(false);
    }
  };

  const confirmProcess = async () => {
    try {
      setProcessing(true);
      await inventoryApi.processSupplierPayment(id, {});
      toast.success('Payment processed');
      setShowProcessModal(false);
      fetchPayment();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to process payment'));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusColor = {
    draft: 'bg-gray-100 text-gray-800',
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    paid: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const sectionHeader = (title, extra) => (
    <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-4 py-2.5">
      <span className="text-sm font-semibold text-gray-800">{title}</span>
      {extra}
    </div>
  );

  const fieldLabel = 'block text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1';
  const fieldValue = 'text-sm font-semibold text-gray-900';

  // Table with per-column alignment (amount columns right-aligned, headers included)
  const table = (columns, rows, rowKey = (r, i) => i) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-white">
          <tr>
            {columns.map(c => (
              <th key={c.label} className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${c.right ? 'text-right' : 'text-left'}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} className="hover:bg-gray-50">
              {columns.map(c => (
                <td key={c.label} className={`px-4 py-3 text-sm whitespace-nowrap ${c.right ? 'text-right tabular-nums' : 'text-left'} ${c.className || 'text-gray-700'}`}>{c.render(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const card = (title, body, extra) => (
    <div className="border border-gray-200 rounded-xl mb-5 overflow-hidden bg-white shadow-sm">
      {sectionHeader(title, extra)}
      {body}
    </div>
  );

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (loading) {
    if (isModal) {
      return (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl bg-white shadow-2xl rounded-2xl overflow-hidden p-10">
            <LoadingSpinner />
          </div>
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  if (!payment) {
    return isModal ? null : (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500">Payment not found.</div>
    );
  }

  const summary = payment.summary || {};
  const nonZero = (n) => Math.abs(Number(n) || 0) > 0.0001;
  const isOpen = ['pending', 'draft'].includes(payment.status);

  // ── Content sections ───────────────────────────────────────────────────────
  const paymentInfoSection = card('Payment Information', (
    <div className="px-4 py-4 space-y-4">
      {/* 1. Document */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        <div>
          <label className={fieldLabel}>Voucher No.</label>
          <p className={fieldValue}>{payment.paymentVoucherNo || '—'}</p>
        </div>
        <div>
          <label className={fieldLabel}>Payment Date</label>
          <p className={fieldValue}>{formatDate(payment.paymentDate)}</p>
        </div>
        <div>
          <label className={fieldLabel}>Status</label>
          <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusColor[payment.status] || 'bg-gray-100 text-gray-800'}`}>
            {payment.status?.toUpperCase()}
          </span>
        </div>
        <div>
          <label className={fieldLabel}>GL Entry</label>
          <p className={fieldValue}>
            {payment.glTransaction?.docCode || payment.glTransaction?.transactionId
              ? <span className="text-green-700">Posted{payment.glTransaction?.docCode ? ` · ${payment.glTransaction.docCode}` : ''}</span>
              : ['approved', 'paid'].includes(payment.status) ? <span className="text-amber-700">Not posted</span> : <span className="text-gray-400 font-medium">On approval</span>}
          </p>
        </div>
      </div>

      {/* 2. Supplier & payment */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 pt-4 border-t border-gray-100">
        <div className="col-span-2">
          <label className={fieldLabel}>Supplier</label>
          <p className={fieldValue}>
            {payment.supplier?.name || '—'}
            {payment.supplier?.code && <span className="ml-2 text-xs font-normal text-gray-400">{payment.supplier.code}</span>}
          </p>
        </div>
        <div>
          <label className={fieldLabel}>Payment Type</label>
          <p className={`${fieldValue} capitalize`}>{payment.paymentType?.replace(/_/g, ' ') || '—'}</p>
        </div>
        <div>
          <label className={fieldLabel}>Payment Method</label>
          <p className={`${fieldValue} capitalize`}>{payment.paymentMethod?.replace(/_/g, ' ') || '—'}</p>
        </div>
        <div className="col-span-2">
          <label className={fieldLabel}>Location</label>
          <p className={fieldValue}>{payment.location?.name || '—'}</p>
        </div>
      </div>

      {payment.notes && (
        <div className="pt-4 border-t border-gray-100">
          <label className={fieldLabel}>Notes</label>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.notes}</p>
        </div>
      )}
    </div>
  ));

  const grnSection = payment.grnAllocations?.length > 0 && card(`GRN Allocations (${payment.grnAllocations.length})`, table([
    { label: 'GRN Number', render: a => a.grnNo || a.grn?.grnNumber || '—', className: 'font-medium text-gray-900' },
    { label: 'GRN Date', render: a => formatDate(a.grnDate || a.grn?.receiptDate) },
    { label: 'Due Date', render: a => formatDate(a.dueDate) },
    { label: 'GRN Amount', right: true, render: a => money(a.grnAmount) },
    { label: 'Outstanding', right: true, render: a => money(a.outstandingBalance) },
    { label: 'Paying', right: true, render: a => money(a.amountPaid), className: 'font-semibold text-indigo-700' },
    { label: 'Balance After', right: true, render: a => money(a.remainingBalance ?? (a.outstandingBalance || 0) - (a.amountPaid || 0)) }
  ], payment.grnAllocations));

  const srnSection = payment.srnAllocations?.length > 0 && card(`Supplier Return Set-offs (${payment.srnAllocations.length})`, table([
    { label: 'Return No.', render: a => a.srnNo || a.srnId?.returnNoteNumber || '—', className: 'font-medium text-gray-900' },
    { label: 'Date', render: a => formatDate(a.srnDate || a.srnId?.returnDate) },
    { label: 'Return Value', right: true, render: a => money(a.returnValue) },
    { label: 'Set-off', right: true, render: a => `-${money(a.amountSetoff)}`, className: 'font-semibold text-orange-600' }
  ], payment.srnAllocations));

  const debitNoteSection = payment.debitNoteAllocations?.length > 0 && card(`Debit Note Set-offs (${payment.debitNoteAllocations.length})`, table([
    { label: 'Debit Note No.', render: a => a.debitNoteNo || a.debitNoteId?.debitNoteNumber || '—', className: 'font-medium text-gray-900' },
    { label: 'Date', render: a => formatDate(a.debitDate || a.debitNoteId?.debitDate) },
    { label: 'Note Value', right: true, render: a => money(a.noteValue) },
    { label: 'Set-off', right: true, render: a => `-${money(a.amountSetoff)}`, className: 'font-semibold text-amber-600' }
  ], payment.debitNoteAllocations));

  const isAdvanceType = ['advance', 'on_account'].includes(payment.paymentType);
  const advanceSection = isAdvanceType && card(payment.paymentType === 'advance' ? 'Advance' : 'On-account Payment', (
    <div className="px-4 py-3 space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
        <div><label className={fieldLabel}>Amount</label><p className={fieldValue}>{money(payment.advance?.amount)}</p></div>
        <div><label className={fieldLabel}>Set Off</label><p className={fieldValue}>{money(payment.advance?.appliedAmount)}</p></div>
        <div><label className={fieldLabel}>Still Open</label><p className={`${fieldValue} text-teal-700`}>{['approved', 'paid'].includes(payment.status) ? money(payment.advance?.remainingAmount) : 'on approval'}</p></div>
        <div>
          <label className={fieldLabel}>{payment.paymentType === 'advance' ? 'Purchase Order / Account' : 'Account'}</label>
          <p className={fieldValue}>
            {payment.advance?.purchaseOrderNumber || (payment.paymentType === 'advance' ? 'No PO' : '')}
            <span className="block text-xs font-normal text-gray-500">{payment.advance?.ledgerAccountLabel || 'Supplier ledger'}</span>
          </p>
        </div>
      </div>
      {(payment.advance?.applications || []).length > 0 && (
        <div className="text-xs text-gray-600">
          Set off in: {payment.advance.applications.map(x => `${x.paymentNumber} (${money(x.amount)})`).join(', ')}
        </div>
      )}
    </div>
  ));

  const advanceSetoffSection = payment.advanceAllocations?.length > 0 && card(`Advance Set-offs (${payment.advanceAllocations.length})`, table([
    { label: 'Payment No.', render: a => a.advanceNo || '—', className: 'font-medium text-gray-900' },
    { label: 'Type', render: a => (a.advanceType === 'on_account' ? 'On account' : 'Advance') },
    { label: 'Date', render: a => formatDate(a.advanceDate) },
    { label: 'PO', render: a => a.purchaseOrderNumber || '—' },
    { label: 'Set-off', right: true, render: a => `-${money(a.amountSetoff)}`, className: 'font-semibold text-teal-600' }
  ], payment.advanceAllocations));

  const paymentModesSection = payment.paymentModes?.length > 0 && card('Payment Modes', table([
    { label: 'Method', render: m => <span className="capitalize">{m.paymentMethod?.replace(/_/g, ' ') || '—'}</span>, className: 'font-medium text-gray-900' },
    { label: 'Ledger Account', render: m => m.ledgerAccountLabel || '—' },
    {
      label: 'Details', render: m => (
        <>
          {m.paymentMethod === 'cheque' && m.chequeDetails?.chequeNo && (
            <span>Chq {m.chequeDetails.chequeNo}{m.chequeDetails.chequeDate ? ` · ${formatDate(m.chequeDetails.chequeDate)}` : ''}{m.chequeDetails.bankName ? ` · ${m.chequeDetails.bankName}` : ''}</span>
          )}
          {m.paymentMethod === 'bank_transfer' && m.transferDetails?.referenceNo && <span>Ref {m.transferDetails.referenceNo}</span>}
          {!m.chequeDetails?.chequeNo && !m.transferDetails?.referenceNo && (m.accountNo || '—')}
        </>
      )
    },
    { label: 'Notes', render: m => m.notes || '—', className: 'text-gray-500' },
    { label: 'Amount', right: true, render: m => money(m.amount), className: 'font-semibold text-green-700' }
  ], payment.paymentModes));

  const summaryRow = (label, value, cls = 'text-gray-900') => (
    <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-gray-200 last:border-b-0">
      <span className="text-gray-600">{label}</span>
      <span className={`font-semibold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
  // Totals block (invoice style): GRN total, then each deduction, then the net paid
  const summarySection = card('Payment Summary', (
    <div className="px-4 py-3 flex justify-end">
      <div className="w-full sm:w-96">
        {summaryRow(isAdvanceType ? (payment.paymentType === 'advance' ? 'Advance Amount' : 'On-account Amount') : 'GRN Total', money(summary.totalPaymentAmount))}
        {nonZero(summary.adjustments) && summaryRow('Adjustments', `${summary.adjustments > 0 ? '+' : ''}${money(summary.adjustments)}`, summary.adjustments > 0 ? 'text-green-600' : 'text-red-600')}
        {nonZero(summary.creditNotesApplied) && summaryRow(
          `Less: Credit Note${payment.creditNote?.reference ? ` ${payment.creditNote.reference}` : ''}${payment.creditNote?.ledgerAccountLabel ? ` → ${payment.creditNote.ledgerAccountLabel}` : ''}`,
          `-${money(summary.creditNotesApplied)}`, 'text-purple-600')}
        {nonZero(summary.totalSRNSetoffs) && summaryRow('Less: Supplier Return Set-offs', `-${money(summary.totalSRNSetoffs)}`, 'text-orange-600')}
        {nonZero(summary.totalDebitNoteSetoffs) && summaryRow('Less: Debit Note Set-offs', `-${money(summary.totalDebitNoteSetoffs)}`, 'text-amber-600')}
        {nonZero(summary.totalAdvanceSetoffs) && summaryRow('Less: Advance Set-offs', `-${money(summary.totalAdvanceSetoffs)}`, 'text-teal-600')}
        <div className="flex items-center justify-between mt-2 px-3 py-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
          <span className="text-sm font-bold text-indigo-900 uppercase tracking-wide">Net Payment</span>
          <span className="text-lg font-bold text-indigo-700 tabular-nums">{money(summary.netPaymentAmount)}</span>
        </div>
      </div>
    </div>
  ));

  const activity = [
    { label: 'Created', by: payment.createdByUserName || payment.preparedBy?.name, at: payment.createdAt, color: 'bg-blue-100 text-blue-600' },
    payment.updatedByUserName && { label: 'Updated', by: payment.updatedByUserName, at: payment.updatedAt, color: 'bg-yellow-100 text-yellow-600' },
    (payment.approvedByUserName || payment.approvedBy) && { label: 'Approved', by: payment.approvedByUserName || payment.approvedBy?.name, at: payment.approvedAt, color: 'bg-green-100 text-green-600' },
    payment.processedBy && { label: 'Processed', by: payment.processedBy?.name, at: payment.processedAt, color: 'bg-purple-100 text-purple-600' },
    payment.reversal?.reversedAt && { label: 'Reversed', by: payment.reversal?.reason, at: payment.reversal.reversedAt, color: 'bg-red-100 text-red-600' }
  ].filter(Boolean);
  const peopleSection = card('Activity', (
    <div className="px-4 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {activity.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
            <span className="text-sm font-semibold">{item.by?.charAt(0)?.toUpperCase() || '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{item.label}</p>
            <p className="text-sm font-medium text-gray-900 truncate" title={item.by || ''}>{item.by || '—'}</p>
            <p className="text-xs text-gray-400">{formatDateTime(item.at)}</p>
          </div>
        </div>
      ))}
    </div>
  ));

  // ── Action buttons ─────────────────────────────────────────────────────────
  const spinner = (
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
  const actionButtons = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {isOpen && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:APPROVE']) && (
        <>
          <button
            onClick={() => openAction(setShowRejectModal)}
            disabled={approving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-300 text-red-700 bg-white hover:bg-red-50 rounded transition-colors disabled:opacity-50"
          >
            Reject
          </button>
          <button
            onClick={() => openAction(setShowApproveModal)}
            disabled={approving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-green-300 text-green-700 bg-white hover:bg-green-50 rounded transition-colors disabled:opacity-50"
          >
            {approving && spinner}
            {approving ? 'Approving…' : 'Approve'}
          </button>
        </>
      )}
      {payment.status === 'approved' && hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:PROCESS']) && (
        <button
          onClick={() => openAction(setShowProcessModal)}
          disabled={processing}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50"
        >
          {processing && spinner}
          {processing ? 'Processing…' : 'Process Payment'}
        </button>
      )}
      {['approved', 'paid', 'cancelled'].includes(payment.status) && (
        <button
          onClick={() => setShowDoubleEntries(true)}
          title="GL double entries of this payment"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 rounded transition-colors"
        >
          Double Entries
        </button>
      )}
      <ChequePrintAction payment={payment} sourceType="SupplierPayment" lines={supplierPaymentChequeLines(payment)} payee={payment.supplier?.name || ''} variant="button" />
      {canPrintSupplierPayment(payment.status) && (
        <button
          onClick={handlePrint}
          title="Print PDF"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded transition-colors"
        >
          Print PDF
        </button>
      )}
      <button
        onClick={handleClose}
        className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
      >
        Close
      </button>
    </div>
  );

  // ── Confirmation modals (ConfirmationModal passes the comment text, not an event) ──
  const confirmationModals = (
    <>
      {showDoubleEntries && (
        <DoubleEntryModal
          documentType="SupplierPayment"
          documentId={payment._id}
          title={payment.paymentVoucherNo}
          onClose={() => setShowDoubleEntries(false)}
        />
      )}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Supplier Payment"
        message={
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              Approve payment <span className="font-semibold text-gray-900">{payment.paymentVoucherNo}</span>?
            </p>
            <p className="text-xs text-gray-500">
              Net amount <span className="font-semibold text-gray-800">{money(summary.netPaymentAmount)}</span>
              {(nonZero(summary.totalDebitNoteSetoffs) || nonZero(summary.totalSRNSetoffs)) && ' · set-offs are drawn from the debit notes'}
            </p>
          </div>
        }
        confirmText="Approve"
        cancelText="Cancel"
        type="success"
        loading={approving}
        showCommentBox={true}
        comment={actionComment}
        onCommentChange={setActionComment}
        commentPlaceholder="Comments (optional)..."
      />

      <ConfirmationModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={confirmReject}
        title="Reject Supplier Payment"
        message={
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              Reject payment <span className="font-semibold text-gray-900">{payment.paymentVoucherNo}</span>?
            </p>
            <p className="text-xs text-orange-600">The payment goes back to draft. A reason is required.</p>
          </div>
        }
        confirmText="Reject"
        cancelText="Cancel"
        type="danger"
        loading={approving}
        showCommentBox={true}
        comment={actionComment}
        onCommentChange={setActionComment}
        commentPlaceholder="Rejection reason (required)..."
        disableConfirm={!actionComment.trim()}
      />

      <ConfirmationModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        onConfirm={confirmProcess}
        title="Process Payment"
        message={
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              Process payment <span className="font-semibold text-gray-900">{payment.paymentVoucherNo}</span> ({money(summary.netPaymentAmount)})?
            </p>
            <p className="text-xs text-red-600 font-medium">This action cannot be undone.</p>
          </div>
        }
        confirmText="Process Payment"
        cancelText="Cancel"
        type="warning"
        loading={processing}
      />
    </>
  );

  const documentsSection = (
    <div className="mb-5">
      <ProcurementDocuments docType="supplier-payments" docId={payment._id}
        canEdit={hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:UPDATE']) || hasPermission(['INVENTORY:SUPPLIER_PAYMENTS:CREATE'])} />
    </div>
  );

  const body = (
    <>
      <PaymentReversalBanner reversal={payment.reversal} className="mb-5" />
      {/* Document order: who / what → what is paid → what is deducted → how it is paid → totals → history */}
      {paymentInfoSection}
      {advanceSection}
      {grnSection}
      {advanceSetoffSection}
      {debitNoteSection}
      {srnSection}
      {paymentModesSection}
      {summarySection}
      {documentsSection}
      {peopleSection}
    </>
  );

  // ── Modal mode ─────────────────────────────────────────────────────────────
  if (isModal) {
    return (
      <>
        <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative w-full max-w-5xl max-h-[92vh] bg-white shadow-2xl flex flex-col rounded-2xl overflow-hidden">
            <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-6 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-white">Payment Details</h3>
                  <p className="text-indigo-100 text-sm truncate">Voucher #{payment.paymentVoucherNo} · {payment.supplier?.name || ''}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${statusColor[payment.status] || 'bg-gray-100 text-gray-800'}`}>
                    {payment.status?.toUpperCase()}
                  </span>
                  <button
                    onClick={handleClose}
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                    aria-label="Close"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 pt-5 pb-1 bg-gray-50">
              {body}
            </div>

            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-gray-50/60">
              {actionButtons}
            </div>
          </div>
        </div>
        {confirmationModals}
      </>
    );
  }

  // ── Page mode (standalone route) ───────────────────────────────────────────
  return (
    <>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 transition-colors" aria-label="Back">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{payment.paymentVoucherNo}</h1>
              <p className="text-sm text-gray-500">Created on {formatDateTime(payment.createdAt)}</p>
            </div>
          </div>
          {actionButtons}
        </div>
        {body}
      </div>
      {confirmationModals}
    </>
  );
};

export default SupplierPaymentView;
